// frontend/src/features/ups/UpsRequestsDriverList.js
// Phase 8.7D: UPS "Requests" tab
//
// Purpose:
// - Show assignment requests created by agencies (status = 'requested').
// - UPS can approve them -> status transitions requested -> confirmed.
// - Once approved, they disappear from Requests and appear in Reserved.
//
// Data source:
// - Uses the same UPS site assignments read model as Reserved/Active.
// - Just passes statuses: ['requested'].
//
// Phase 8.7D cleanup:
// - Do NOT pass agencyId/search into loadSiteAssignments payload (thunk reads from Redux).
// - Keep effect deps on filters so refetch occurs when filters change.

import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { loadSiteAssignments, makeSelectAssignmentsForTab } from '../assignments/assignmentsSlice';
import { selectSelectedSiteId } from '../sites/sitesSlice';
import { selectFilters, bumpStatsRefreshKey } from '../ui/filtersSlice';

import { approveAssignment, cancelAssignment } from '../../services/assignmentService';

export default function UpsRequestsDriverList() {
  const dispatch = useDispatch();

  const siteId = useSelector(selectSelectedSiteId);
  const filters = useSelector(selectFilters);

  // We keep requests separate from reserved:
  // Requests = requested
  const selectRequested = useMemo(() => makeSelectAssignmentsForTab('requested'), []);
  const requested = useSelector(selectRequested);

  const [acting, setActing] = useState(() => new Set());

  useEffect(() => {
    if (!siteId) return;

    dispatch(loadSiteAssignments({ siteId, statuses: ['requested'] }));
  }, [dispatch, siteId, filters?.agencyId, filters?.q]);

  const rows = useMemo(() => {
    // keep consistent with other lists: filter banned rows out (if they exist on the assignment row)
    return (requested || []).filter((r) => !r?.is_banned);
  }, [requested]);

  async function refresh() {
    if (!siteId) return;

    // reload requests + bump stats so other tabs/cards refresh
    dispatch(loadSiteAssignments({ siteId, statuses: ['requested'] }));
    dispatch(bumpStatsRefreshKey());
  }

  const onApprove = async (assignmentId) => {
    if (!siteId) return;
    if (acting.has(assignmentId)) return;

    setActing((prev) => new Set(prev).add(assignmentId));
    try {
      await approveAssignment(siteId, assignmentId);

      // Refresh BOTH lists:
      // - Requests should drop it
      // - Reserved tab (confirmed) will fetch when you click it, but we also bump stats key
      await refresh();
    } catch (e) {
      const status = e?.response?.status;
      const data = e?.response?.data;
      console.error('[UpsRequestsDriverList] approve failed:', {
        assignmentId,
        status,
        data,
        message: e?.message,
      });

      alert(data?.error || data?.message || (status ? `Approve failed (${status})` : 'Approve failed'));
      await refresh();
    } finally {
      setActing((prev) => {
        const next = new Set(prev);
        next.delete(assignmentId);
        return next;
      });
    }
  };

  const onReject = async (assignmentId) => {
    if (!siteId) return;
    if (acting.has(assignmentId)) return;

    setActing((prev) => new Set(prev).add(assignmentId));
    try {
      // Reuse cancel endpoint as a "reject" for now (requested -> cancelled)
      await cancelAssignment(siteId, assignmentId);
      await refresh();
    } catch (e) {
      const status = e?.response?.status;
      const data = e?.response?.data;
      console.error('[UpsRequestsDriverList] reject/cancel failed:', {
        assignmentId,
        status,
        data,
        message: e?.message,
      });

      alert(data?.error || data?.message || (status ? `Reject failed (${status})` : 'Reject failed'));
      await refresh();
    } finally {
      setActing((prev) => {
        const next = new Set(prev);
        next.delete(assignmentId);
        return next;
      });
    }
  };

 return (
  <div className="my-6">
    <div className="mb-4">
      <h3 className="text-2xl font-bold text-gray-800">
        Pending Requests
      </h3>
    </div>

    <div className="overflow-x-auto">
      <table className="min-w-full bg-white rounded-lg shadow overflow-hidden">
        <thead className="bg-gray-50">
          <tr>
            {['Name', 'Vehicle Reg.', 'Agency', 'Submitted', 'Actions'].map((col) => (
              <th
                key={col}
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                {col}
              </th>
            ))}
          </tr>
        </thead>

        <tbody className="divide-y divide-gray-200">
          {rows.length === 0 ? (
            <tr>
              <td className="px-6 py-6 text-sm text-gray-500" colSpan={5}>
                No pending requests.
              </td>
            </tr>
          ) : (
            rows.map((r) => (
              <tr key={r.assignment_id || r.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap text-sm">{r?.name}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm">
                  {r?.vehicle_registration_number}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm">
                  {r?.agency_name || r?.agency}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm">
                  {r?.created_at ? new Date(r.created_at).toLocaleString() : '-'}
                </td>

                <td className="px-6 py-4 whitespace-nowrap text-sm">
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => onApprove(Number(r.assignment_id || r.id))}
                      disabled={acting.has(Number(r.assignment_id || r.id))}
                      className={`px-3 py-1 rounded bg-green-600 text-white text-xs hover:bg-green-700 ${
                        acting.has(Number(r.assignment_id || r.id))
                          ? 'opacity-60 cursor-not-allowed'
                          : ''
                      }`}
                      title="Approve (requested -> confirmed)"
                    >
                      Approve
                    </button>

                    <button
                      onClick={() => onReject(Number(r.assignment_id || r.id))}
                      disabled={acting.has(Number(r.assignment_id || r.id))}
                      className={`px-3 py-1 rounded bg-gray-200 text-gray-900 text-xs hover:bg-gray-300 ${
                        acting.has(Number(r.assignment_id || r.id))
                          ? 'opacity-60 cursor-not-allowed'
                          : ''
                      }`}
                      title="Reject (cancel request)"
                    >
                      Reject
                    </button>
                  </div>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  </div>
);
}